import { useQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  AppContent,
  AppPage,
  Breadcrumbs,
  ErrorComponents,
  OpenSidebarButton,
  PageHeaderV2,
  isAccess,
  isResourceAccess,
  useFeatureToggle,
  useTitle,
  withApollo,
  useRecentlyViewed,
} from '@css-xp/base';
// @ts-ignore
import InvoiceGroupIcon from '@css-xp/base/assets/img/icon-folder.svg';
import {
  GetUpdatePaymentMethodLinkPane,
  ManagePaymentMethodsSidebar,
  SetCoBillDate,
  SetPaymentMethod,
} from '@css-xp/base/common-sidebars';
import { CloudSiteLinkedSubscriptionTab } from '@css-xp/base/common-tabs';
import { abgGraphqlClient as graphqlClient } from '@atlassian/temp-client-utils';
import {
  FetchInvoiceGroupV2Query,
  FetchInvoiceGroupV2QueryVariables,
  InvoiceGroupProduct,
} from '@css-xp/graphtypes/src/generated-types-new';
import { AnalyticsEventSource, ScreenEventAnalytics } from '@atlassian/temp-analytics';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { updateDefaultPaymentMethod } from '@atlassian/temp-proxies';
import { Permissions, Resources, TRANSACTION_ACCOUNT_PATH_2 } from '@atlassian/cssxp-typescommon';
import {
  ENABLE_CREDIT_MANAGEMENT,
  ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE,
  ENABLE_RECENTLY_VISITED_HISTORY,
  removeContentScrollingHack,
} from '@atlassian/cssxp-utils-generic';
import { isConfluenceProduct, isJiraProduct } from '@atlassian/cssxp-utils-product';
import { TaxExemptionAuditLogs } from '../../transaction-account/src/sidebar-panels/tax-exemption-audit-logs';
import InvoiceDetailsTabIG from './Invoice-detail-tab-ig';
import { InvoiceGroupSummary } from './components/invoice-group-summary';
import { EditInvoiceGroupPane } from './components/sidebar/edit-invoice-group-pane';
import { EditInvoiceRecipientPane } from './components/sidebar/invoice-recipient-pane';
import './invoice-group-page.scss';
import { fetchInvoiceGroupQueryV2 } from './queries/fetch-invoice-group';
import { fetchProductsIDForInvoiceGroup } from './queries/fetch-product-id-for-billing-group';
import { SubscriptionTabIg } from './subscription-tab-ig';
// @ts-ignore
import AtlassianPartnerIcon from '@css-xp/base/assets/img/icon-atlassian-partner.svg';

/* eslint-disable no-unused-vars */
export enum SIDEBAR {
  EDIT_INVOICE_GROUP = 'edit-invoice-group',
  GET_PAYMENT_METHOD_LINK = 'get-payment-method-link',
  SET_CARD_PAYMENT_DEFAULT = 'setCardPaymentDefault',
  MANAGE_PAYMENT_METHODS = 'manage-payment-methods',
  UPDATE_INVOICE_RECIPIENTS = 'update-invoice-recipients',
  SET_BILL_DATE = 'set-bill-date',
  TAX_EXEMPTION_AUDIT_LOGS = 'taxExemptionAuditLogs',
}

/* eslint-disable no-unused-vars */

const allowedSidebars = {
  [SIDEBAR.EDIT_INVOICE_GROUP]: EditInvoiceGroupPane,
  [SIDEBAR.GET_PAYMENT_METHOD_LINK]: GetUpdatePaymentMethodLinkPane,
  [SIDEBAR.SET_CARD_PAYMENT_DEFAULT]: SetPaymentMethod,
  [SIDEBAR.MANAGE_PAYMENT_METHODS]: ManagePaymentMethodsSidebar,
  [SIDEBAR.UPDATE_INVOICE_RECIPIENTS]: EditInvoiceRecipientPane,
  [SIDEBAR.SET_BILL_DATE]: withApollo(SetCoBillDate),
  [SIDEBAR.TAX_EXEMPTION_AUDIT_LOGS]: TaxExemptionAuditLogs,
};

export const InvoiceGroupPage = ({ appPageClass, title }: { appPageClass?: string; title?: string }) => {
  useEffect(() => {
    removeContentScrollingHack();
  }, []);

  const [isEnabled] = useFeatureToggle();
  const isCreditManagementEnabled = isEnabled(ENABLE_CREDIT_MANAGEMENT);
  const { id } = useParams<{ id: string }>();
  useTitle(id, title);
  const isPaymentOnTermsMethodAllowed = isAccess(Permissions.PAYMENT_METHOD_ON_TERM_WRITE);
  const [paymentOnTermsDisabled, setPaymentOnTermsDisabled] = useState<boolean>(!isPaymentOnTermsMethodAllowed);
  const { data, loading, error, refetch } = useQuery<FetchInvoiceGroupV2Query, FetchInvoiceGroupV2QueryVariables>(
    fetchInvoiceGroupQueryV2,
    {
      variables: {
        id,
      },
    }
  );

  const { updateRecentlyViewed } = useRecentlyViewed();
  React.useEffect(() => {
    if (data && isEnabled(ENABLE_RECENTLY_VISITED_HISTORY)) {
      updateRecentlyViewed({
        entityName: title?.toLowerCase().trim().replace(/\s+/g, '_'),
        metadata: {
          invoiceGroupDisplayName: getInvoiceGroupName(),
          paymentMethods: data.invoiceGroup?.defaultPaymentMethod?.type,
        },
      });
    }
  }, [data, title]);

  // Identifies if any of the products are jira product and should payment or terms be enabled or disbaled for IG.
  React.useEffect(() => {
    if (isCreditManagementEnabled) return;
    (async () => {
      const { data } = await graphqlClient().query({
        query: fetchProductsIDForInvoiceGroup,
        variables: {
          id: id ?? '',
          fetchEntitlementProductIDs: true,
          skip: !id,
        },
        fetchPolicy: 'no-cache',
      });
      const productRecords = data?.invoiceGroup?.products.nodes as Array<InvoiceGroupProduct>;
      let disablePaymentOnTermFlag: Array<InvoiceGroupProduct> = [];
      if (productRecords) {
        disablePaymentOnTermFlag = productRecords.filter(
          (item) => isConfluenceProduct(item.productId) || isJiraProduct(item.productId)
        );
      }
      if (disablePaymentOnTermFlag.length) {
        setPaymentOnTermsDisabled(true);
      }
    })();
  }, []);

  if (loading) return <LoadingIndicator />;
  if (error) return <ErrorComponents.GenericErrorPage error={error} />;
  if (!data?.invoiceGroup) {
    return (
      <ErrorComponents.ErrorPanel
        error={new ErrorComponents.NotFoundError(`Unable to find invoice group with ID: ${id}`)}
      />
    );
  }
  const invoiceGroupDisplayName = data.invoiceGroup?.name;
  const transactionAccountId = data.invoiceGroup.transactionAccount?.id;
  const transactionAccountNumber = data.invoiceGroup?.transactionAccountNumber;
  const isPartnerAccount = !!data.invoiceGroup?.transactionAccount?.partnerAccountId;

  const getInvoiceGroupName = () => {
    if (!invoiceGroupDisplayName || invoiceGroupDisplayName?.toLowerCase() === 'none') {
      return 'Unnamed billing profile';
    }
    return invoiceGroupDisplayName;
  };

  const renderSubHeader = () => {
    return (
      <div className="invoice-group-subheader">
        {isPartnerAccount && (
          <span data-testid="partner-logo">
            {/*// @ts-ignore */}
            <img src={AtlassianPartnerIcon} height={'15px'} alt="Atlassian partner icon" />
          </span>
        )}
      </div>
    );
  };

  return (
    <AppPage id={'invoice-group-page'} allowedSidebars={allowedSidebars} className={appPageClass}>
      <ScreenEventAnalytics
        event={{
          name: AnalyticsEventSource.INVOICE_GROUP_SCREEN,
          attributes: {
            invoiceGroupDisplayName,
            transactionAccountId,
            id,
            entityName: title,
          },
        }}
      />
      <Breadcrumbs
        items={[
          {
            title: transactionAccountNumber || 'Billing account',
            href: `${TRANSACTION_ACCOUNT_PATH_2}/${encodeURIComponent(transactionAccountNumber || '')}`,
          },
          { title: 'Billing profile', href: undefined },
          { title: getInvoiceGroupName() || '', href: '#' },
        ]}
      />
      <AppContent>
        <PageHeaderV2
          /*// @ts-ignore */
          icon={<img src={InvoiceGroupIcon} height={'30px'} alt="IG" />}
          header={getInvoiceGroupName()}
          subheader={renderSubHeader()}
          actions={
            isResourceAccess(Resources.INVOICE_GROUP_ALL) && (
              <>
                {isAccess(Permissions.INVOICE_GROUP_WRITE) && (
                  <OpenSidebarButton
                    sidebar={SIDEBAR.EDIT_INVOICE_GROUP}
                    appearance="primary"
                    sidebarOptions={{
                      sidebarWidth: '600px',
                      isOverlay: true,
                    }}
                    payload={{ ...data?.invoiceGroup, refetch }}
                    testId="edit-invoice-group-button"
                    label="Edit billing profile"
                  >
                    Edit billing profile
                  </OpenSidebarButton>
                )}
              </>
            )
          }
        />
        <InvoiceGroupSummary
          invoiceGroup={data}
          managePaymentMethodsSidebar={SIDEBAR.MANAGE_PAYMENT_METHODS}
          refetch={refetch}
          submitAction={updateDefaultPaymentMethod}
          isTermPaymentMethodAllowed={!paymentOnTermsDisabled}
        />
        <div className="invoice-group-container">
          {isEnabled(ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE) ? (
            <CloudSiteLinkedSubscriptionTab
              billingProfileId={id}
              label="List of subscriptions linked to this billing profile"
            />
          ) : (
            <SubscriptionTabIg
              invoiceGroupId={id}
              label="Linked subscriptions"
              setBillDateSidebar={SIDEBAR.SET_BILL_DATE}
            />
          )}

          <InvoiceDetailsTabIG id={id} transactionAccountId={transactionAccountId} />
        </div>
      </AppContent>
    </AppPage>
  );
};
